import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getTaskById } from '../../APIManager/TaskManager'
import { notification } from '../Notifications'
import { activeSlotsPolling, BASE_INTERVAL_MS, IS_MOBILE, MAX_INTERVAL_MS } from '../../hooks/useSlotBackgroundTask'
import { useUserRole } from '../../hooks/useUserRole'
import { formatSlotTaskToast } from '../../utils/formatSlotTaskToast'
import type { SlotTaskKind, TaskPollStatus } from '../../models/Task'

const TERMINAL_STATUSES = new Set(['SUCCESSFUL', 'WARNING', 'ERROR', 'COMPLETED', 'FAILED', 'REVOKED'])

interface ClaimedTask {
  slotId: string
  taskId: string
  message: string
  successMessage?: string
  /**
   * Ids still to poll once `taskId` succeeds. Written by `useSlotBackgroundTask` when one action
   * enqueues several tasks; this poller has to carry the queue too, or navigating away from the
   * page that started it reports the first task as the whole run.
   */
  queuedTaskIds?: string[]
  /**
   * Carried through because the toast for some tasks depends on which operation ran, and this
   * poller is the path taken once the page that started the task is gone. Dropping it here reported
   * a bulk reopen or go-live in generic copy — worse, on the `SUCCESSFUL` a partial run returns, as
   * a clean success with no mention of the sections that were refused.
   */
  kind?: SlotTaskKind
}

const scanUnclaimedTasks = (): ClaimedTask[] => {
  const tasks: ClaimedTask[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key?.startsWith('slotTask_')) continue
    const slotId = key.replace('slotTask_', '')
    if (activeSlotsPolling.has(slotId)) continue
    try {
      const stored = JSON.parse(localStorage.getItem(key) ?? '') as { taskId?: string, message?: string, successMessage?: string, queuedTaskIds?: string[], kind?: SlotTaskKind }
      if (stored?.taskId) {
        tasks.push({ slotId, taskId: stored.taskId, message: stored.message ?? '', successMessage: stored.successMessage, queuedTaskIds: stored.queuedTaskIds, kind: stored.kind })
      }
    } catch {}
  }
  return tasks
}

interface SingleTaskPollerProps {
  task: ClaimedTask
  onDone: (slotId: string) => void
  onAdvance: (slotId: string, next: ClaimedTask) => void
}

const SingleTaskPoller: React.FC<SingleTaskPollerProps> = ({ task, onDone, onAdvance }) => {
  const { isGlobalRole } = useUserRole()
  const pollCountRef = useRef(0)
  const handledRef = useRef(false)
  const [isVisible, setIsVisible] = useState(IS_MOBILE || document.visibilityState === 'visible')

  // This component stays mounted across a queue advance (same slot, next id), so the guard that
  // stops one terminal payload being reported twice has to reopen for the id it moves to.
  useEffect(() => {
    handledRef.current = false
    pollCountRef.current = 0
  }, [task.taskId])

  // Desktop only: only poll when in the foreground; reset backoff on focus.
  useEffect(() => {
    if (IS_MOBILE) return
    const handleVisibilityChange = (): void => {
      const visible = document.visibilityState === 'visible'
      setIsVisible(visible)
      if (visible) pollCountRef.current = 0
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => { document.removeEventListener('visibilitychange', handleVisibilityChange) }
  }, [])

  const { data } = useQuery({
    queryKey: ['globalSlotTask', task.slotId, task.taskId],
    queryFn: async () => await getTaskById(task.taskId),
    enabled: isVisible && isGlobalRole,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status as string | undefined
      if (status != null && TERMINAL_STATUSES.has(status)) return false
      return Math.min(BASE_INTERVAL_MS * Math.pow(2, pollCountRef.current), MAX_INTERVAL_MS)
    }
  })

  useEffect(() => {
    if (!data) return
    const status = data?.data?.status as string | undefined
    if (!status) return
    if (!TERMINAL_STATUSES.has(status)) {
      pollCountRef.current += 1
      return
    }
    if (handledRef.current) return
    handledRef.current = true
    const storageKey = `slotTask_${task.slotId}`
    // localStorage is synchronous: this read-then-remove is race-free within the JS event loop.
    if (localStorage.getItem(storageKey) === null) { onDone(task.slotId); return }

    const pollStatus = status as TaskPollStatus | 'COMPLETED' | 'FAILED' | 'REVOKED'
    const mappedStatus: Exclude<TaskPollStatus, 'PENDING'> =
      pollStatus === 'SUCCESSFUL' || pollStatus === 'WARNING' || pollStatus === 'ERROR'
        ? pollStatus
        : pollStatus === 'COMPLETED'
          ? 'SUCCESSFUL'
          : 'ERROR'

    // Mid-queue: hand the next id back and report nothing yet. A failure ends the queue instead,
    // matching `useSlotBackgroundTask` — the run is already reported as unfinished.
    const [nextTaskId, ...restTaskIds] = task.queuedTaskIds ?? []
    if (mappedStatus === 'SUCCESSFUL' && nextTaskId) {
      const next: ClaimedTask = { ...task, taskId: nextTaskId, queuedTaskIds: restTaskIds }
      localStorage.setItem(storageKey, JSON.stringify({
        taskId: next.taskId,
        message: next.message,
        successMessage: next.successMessage,
        queuedTaskIds: restTaskIds.length > 0 ? restTaskIds : undefined,
        kind: next.kind
      }))
      onAdvance(task.slotId, next)
      return
    }
    localStorage.removeItem(storageKey)

    const toast = formatSlotTaskToast({
      status: mappedStatus,
      data: data?.data?.data,
      taskMetadata: data?.data?.taskMetadata,
      fallbackMessage: task.successMessage || task.message,
      kind: task.kind
    })
    notification(toast.title, {
      variant: toast.variant,
      subTitle: toast.subTitle,
      persist: toast.variant !== 'success'
    })
    onDone(task.slotId)
  }, [data, task, onDone, onAdvance])

  return null
}

export const GlobalSlotTaskPoller: React.FC = () => {
  const [unclaimedTasks, setUnclaimedTasks] = useState<ClaimedTask[]>([])

  useEffect(() => {
    const rescan = (): void => {
      setUnclaimedTasks(scanUnclaimedTasks())
    }
    rescan()
    window.addEventListener('slotTask:released', rescan)
    if (IS_MOBILE) {
      return () => { window.removeEventListener('slotTask:released', rescan) }
    }
    const handleStorage = (e: StorageEvent): void => {
      if (e.key?.startsWith('slotTask_')) rescan()
    }
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') rescan()
    }
    window.addEventListener('storage', handleStorage)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.removeEventListener('slotTask:released', rescan)
      window.removeEventListener('storage', handleStorage)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const handleDone = useCallback((slotId: string): void => {
    setUnclaimedTasks(prev => prev.filter(t => t.slotId !== slotId))
  }, [])

  const handleAdvance = useCallback((slotId: string, next: ClaimedTask): void => {
    setUnclaimedTasks(prev => prev.map(t => (t.slotId === slotId ? next : t)))
  }, [])

  return (
    <>
      {unclaimedTasks.map(task => (
        <SingleTaskPoller key={task.slotId} task={task} onDone={handleDone} onAdvance={handleAdvance} />
      ))}
    </>
  )
}
